import React, { useState, useEffect, useCallback } from 'react';
import API from 'shared/api';
import { FiX, FiSave, FiRefreshCw, FiChevronDown } from 'react-icons/fi';
import { SS } from './syncUtils';

/* ── Campos canónicos que se muestran en el editor ── */

const CAMPOS_PRODUCTOS = [
  { k: 'c_codigo', label: 'Codigo', obligatorio: true },
  { k: 'c_descri', label: 'Descripcion', obligatorio: true },
  { k: 'n_precio1', label: 'Precio de venta', obligatorio: true },
  { k: 'c_marca', label: 'Marca' },
  { k: 'c_modelo', label: 'Modelo' },
  { k: 'c_departamento', label: 'Departamento' },
  { k: 'c_grupo', label: 'Grupo' },
  { k: 'c_subgrupo', label: 'Subgrupo' },
  { k: 'c_procede', label: 'Procede' },
  { k: 'c_referencia', label: 'Referencia' },
  { k: 'n_costoact', label: 'Costo actual' },
  { k: 'n_costoant', label: 'Costo anterior' },
  { k: 'n_costopro', label: 'Costo promedio' },
  { k: 'n_costorep', label: 'Costo reposicion' },
  { k: 'n_precio2', label: 'Precio 2' },
  { k: 'n_precio3', label: 'Precio 3' },
  { k: 'n_activo', label: 'Activo' },
  { k: 'cu_descripcion_corta', label: 'Descripcion corta' },
];

const CAMPOS_STOCK = [
  { k: 'c_codarticulo', label: 'Código del artículo', obligatorio: true },
  { k: 'c_coddeposito', label: 'Deposito / Sede' },
  { k: 'n_cantidad', label: 'Cantidad en stock', obligatorio: true },
  { k: 'n_cant_comprometida', label: 'Cantidad comprometida' },
  { k: 'n_cant_ordenada', label: 'Cantidad ordenada' },
];

const CAMPOS_DEPARTAMENTOS = [
  { k: 'c_codigo', label: 'Codigo', obligatorio: true },
  { k: 'c_descripcio', label: 'Descripcion', obligatorio: true },
  { k: 'c_grupo', label: 'Grupo' },
  { k: 'c_observacio', label: 'Observacion' },
];

/* ── Helpers ── */

function _columnas_de_tabla(esquema, tablaCualificada) {
  if (!esquema || !esquema.columnas || !tablaCualificada) return [];
  const key = '.' + tablaCualificada;
  return (esquema.columnas && esquema.columnas[key]) || [];
}

function _tablas_disponibles(esquema) {
  if (!esquema || !esquema.tablas) return [];
  const tipo = esquema.tipo_bd || '';
  if (tipo === 'firebird') return esquema.tablas[''] || [];
  const list = [];
  for (const [schema, tablas] of Object.entries(esquema.tablas)) {
    for (const t of tablas) {
      list.push(schema ? `${schema}.${t}` : t);
    }
  }
  return list;
}

/* ── Componente ── */

export default function SyncMappingModal({ sede, onClose, onGuardado }) {
  const [esquema, setEsquema] = useState(null);
  const [mapeo, setMapeo] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null);

  const { sede_codigo } = sede;

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const res = await API.get(`/sync/esquema/${encodeURIComponent(sede_codigo)}`);
      if (res.data.status === 'success') {
        setMapeo(res.data.data.mapeo_tablas || {});
        setEsquema(res.data.data.esquema_descubierto || null);
      }
    } catch (e) {
      setError('No se pudo cargar el esquema de la base de datos.');
    } finally {
      setCargando(false);
    }
  }, [sede_codigo]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const tablas = esquema ? _tablas_disponibles(esquema) : [];

  const setTablaProductos = (v) =>
    setMapeo((m) => ({
      ...m,
      tabla_productos: v,
      mapeo_productos: v ? m.mapeo_productos || {} : null,
    }));
  const setTablaStock = (v) =>
    setMapeo((m) => ({ ...m, tabla_stock: v, mapeo_stock: v ? m.mapeo_stock || {} : null }));
  const setTablaDeptos = (v) =>
    setMapeo((m) => ({
      ...m,
      tabla_departamentos: v,
      mapeo_departamentos: v ? m.mapeo_departamentos || {} : null,
    }));

  const setColProd = (k, v) =>
    setMapeo((m) => ({ ...m, mapeo_productos: { ...m.mapeo_productos, [k]: v || null } }));
  const setColStock = (k, v) =>
    setMapeo((m) => ({ ...m, mapeo_stock: { ...m.mapeo_stock, [k]: v || null } }));
  const setColDepto = (k, v) =>
    setMapeo((m) => ({ ...m, mapeo_departamentos: { ...m.mapeo_departamentos, [k]: v || null } }));

  const guardar = async () => {
    setGuardando(true);
    setError(null);
    try {
      const res = await API.put(`/sync/mapeo/${encodeURIComponent(sede_codigo)}`, {
        sede_codigo,
        mapeo_tablas: mapeo,
      });
      if (res.data.status === 'success') {
        onGuardado && onGuardado();
        onClose();
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'No se pudo guardar el mapeo.');
    } finally {
      setGuardando(false);
    }
  };

  if (cargando) {
    return (
      <div style={SS.overlay} onClick={onClose}>
        <div
          style={{ ...SS.card, maxWidth: '440px', textAlign: 'center' }}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={{ fontSize: '14px', color: '#6b7280' }}>Cargando esquema...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={SS.overlay} onClick={onClose}>
      <div
        className="sync-modal"
        style={{
          background: '#fff',
          borderRadius: '18px',
          width: '100%',
          maxWidth: '620px',
          maxHeight: '92vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ padding: '20px 24px 0', flexShrink: 0 }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '6px',
            }}
          >
            <div style={{ fontSize: '16px', fontWeight: 700, color: '#111827' }}>
              Editar mapeo de sincronización
            </div>
            <button
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: '#9ca3af',
                padding: '4px',
              }}
            >
              <FiX size={20} />
            </button>
          </div>
          <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '16px' }}>
            Asigna cada campo de Agorax a la columna correspondiente en tu base de datos.
            {!esquema && ' No se encontro el esquema descubierto. Ejecuta el instalador primero.'}
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: '0 24px 20px', overflowY: 'auto', flex: 1 }}>
          {error && (
            <div
              style={{
                padding: '10px 14px',
                background: '#fef2f2',
                borderRadius: '10px',
                marginBottom: '14px',
                fontSize: '13px',
                color: '#b91c1c',
              }}
            >
              {error}
            </div>
          )}

          {!tablas.length && (
            <div
              style={{ textAlign: 'center', padding: '40px 0', color: '#9ca3af', fontSize: '13px' }}
            >
              No hay tablas descubiertas. El sincronizador debe ejecutarse al menos una vez para
              detectar el esquema.
            </div>
          )}

          {/* ── PRODUCTOS ── */}
          {tablas.length > 0 && (
            <MapeoSection
              titulo="Productos"
              tablaKey="tabla_productos"
              tablaVal={mapeo.tabla_productos || ''}
              setTabla={setTablaProductos}
              mapeoVal={mapeo.mapeo_productos || {}}
              campos={CAMPOS_PRODUCTOS}
              tablas={tablas}
              esquema={esquema}
              setCol={setColProd}
            />
          )}

          {/* ── STOCK ── */}
          {tablas.length > 0 && (
            <MapeoSection
              titulo="Stock (existencias)"
              tablaKey="tabla_stock"
              tablaVal={mapeo.tabla_stock || ''}
              setTabla={setTablaStock}
              mapeoVal={mapeo.mapeo_stock || {}}
              campos={CAMPOS_STOCK}
              tablas={tablas}
              esquema={esquema}
              setCol={setColStock}
              desactivar={() => setMapeo((m) => ({ ...m, tabla_stock: '', mapeo_stock: null }))}
            />
          )}

          {/* ── DEPARTAMENTOS ── */}
          {tablas.length > 0 && (
            <MapeoSection
              titulo="Departamentos"
              tablaKey="tabla_departamentos"
              tablaVal={mapeo.tabla_departamentos || ''}
              setTabla={setTablaDeptos}
              mapeoVal={mapeo.mapeo_departamentos || {}}
              campos={CAMPOS_DEPARTAMENTOS}
              tablas={tablas}
              esquema={esquema}
              setCol={setColDepto}
              desactivar={() =>
                setMapeo((m) => ({ ...m, tabla_departamentos: '', mapeo_departamentos: null }))
              }
            />
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '0 24px 20px',
            flexShrink: 0,
            display: 'flex',
            gap: '10px',
            justifyContent: 'flex-end',
            borderTop: '1px solid #f3f4f6',
            paddingTop: '16px',
          }}
        >
          <button
            onClick={async () => {
              setGuardando(true);
              try {
                await API.post('/sync/comando', { sede_codigo, comando: 'descubrir_esquema' });
                setTimeout(() => cargar(), 3000); // esperar que el agente responda
              } catch (e) {
                setError('No se pudo enviar el comando. El agente debe estar en linea.');
              } finally {
                setGuardando(false);
              }
            }}
            style={SS.btnOutline}
          >
            <FiRefreshCw size={14} /> Re-detectar esquema
          </button>
          <button
            onClick={guardar}
            disabled={guardando || !tablas.length}
            style={{ ...SS.btnPrimary, opacity: guardando || !tablas.length ? 0.5 : 1 }}
          >
            <FiSave size={14} /> {guardando ? 'Guardando...' : 'Guardar mapeo'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Sub-componente: seccion de mapeo (productos / stock / deptos) ── */

function MapeoSection({
  titulo,
  tablaVal,
  setTabla,
  mapeoVal,
  campos,
  tablas,
  esquema,
  setCol,
  desactivar,
}) {
  const [abierto, setAbierto] = useState(true);
  const columnas = _columnas_de_tabla(esquema, tablaVal);

  const toggle = () => setAbierto((v) => !v);

  return (
    <div
      style={{
        marginBottom: '14px',
        border: '1px solid #eef0f4',
        borderRadius: '14px',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          padding: '12px 16px',
          background: '#f9fafb',
          cursor: 'pointer',
        }}
        onClick={toggle}
      >
        <span
          style={{
            fontSize: '12px',
            transition: 'transform 0.2s',
            transform: abierto ? 'rotate(90deg)' : 'rotate(0deg)',
          }}
        >
          ▶
        </span>
        <div style={{ flex: 1, fontSize: '13px', fontWeight: 700, color: '#111827' }}>{titulo}</div>
        <select
          value={tablaVal}
          onChange={(e) => {
            e.stopPropagation();
            setTabla(e.target.value);
          }}
          onClick={(e) => e.stopPropagation()}
          style={{
            ...SS.input,
            width: 'auto',
            minWidth: '160px',
            padding: '8px 12px',
            fontSize: '12px',
            appearance: 'auto',
          }}
        >
          <option value="">(no usar)</option>
          {tablas.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
        {desactivar && tablaVal && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              desactivar();
            }}
            title="No sincronizar está tabla"
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: '#9ca3af',
              padding: '4px',
            }}
          >
            <FiX size={15} />
          </button>
        )}
      </div>

      {/* Body */}
      {abierto && tablaVal && (
        <div style={{ padding: '12px 16px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {campos.map(({ k, label, obligatorio }) => (
              <div key={k} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div
                  style={{
                    width: '150px',
                    flexShrink: 0,
                    fontSize: '12px',
                    color: '#4a4d59',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}
                >
                  {label}
                  {obligatorio && <span style={{ color: '#dc2626', fontSize: '10px' }}>*</span>}
                  {!obligatorio && (
                    <span style={{ color: '#9ca3af', fontSize: '10px', fontWeight: 400 }}>
                      (opc)
                    </span>
                  )}
                </div>
                <span style={{ color: '#9ca3af', fontSize: '12px' }}>→</span>
                <SelectCol
                  value={mapeoVal[k] || ''}
                  onChange={(v) => setCol(k, v)}
                  columnas={columnas}
                  obligatorio={obligatorio}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function SelectCol({ value, onChange, columnas, obligatorio }) {
  return (
    <div style={{ position: 'relative', flex: 1 }}>
      <select
        value={value || ''}
        onChange={(e) => onChange(e.target.value || null)}
        style={{
          ...SS.input,
          appearance: 'none',
          cursor: 'pointer',
          paddingRight: '32px',
          color: value ? '#059669' : '#9ca3af',
          borderColor: obligatorio && !value ? '#fca5a5' : value ? '#a7f3d0' : undefined,
          background: value ? '#f0fdf4' : undefined,
        }}
      >
        <option value="">{obligatorio ? '-- Seleccionar --' : '(sin asignar)'}</option>
        {columnas.map((c) => (
          <option key={c} value={c}>
            {c}
          </option>
        ))}
      </select>
      <FiChevronDown
        size={14}
        color="#9ca3af"
        style={{
          position: 'absolute',
          right: '10px',
          top: '50%',
          transform: 'translateY(-50%)',
          pointerEvents: 'none',
        }}
      />
    </div>
  );
}
